import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTransactions, useTracking, useUI, useForm } from "../../hooks";
import { Tracking, Transaction } from "../../types";
import { SelectField } from "../SelectField/SelectField";
import { FormField } from "../FormField/FormField";
import { EmptyState } from "../EmptyState/EmptyState";
import Text from "../Text/Text";
import { TimeRemainingIndicator } from "./TimeRemainingIndicator";
import { isTrackingExpired } from "../../utils/timeCalculations";
import { CameraScanner, CameraScanButton, ScanResult } from "../CameraScanner";
import './PackageTracking.css';

interface PackageTrackingProps {
  transactionId?: string;
}

export const PackageTracking: React.FC<PackageTrackingProps> = ({ transactionId }) => {
    console.log('PackageTracking: Rendering with transactionId =', transactionId);
    
    const navigate = useNavigate();
    const { transactions, getTransactions } = useTransactions();
    const { addTrackingInfo, validateTrackingNumber: validateTracking } = useTracking();
    const { showToast } = useUI();
    const [selectedTransaction, setSelectedTransaction] = useState<string>(transactionId || '');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [validationResult, setValidationResult] = useState<{
      valid: boolean;
      used: boolean;
      message: string;
    } | null>(null);
    const [isCameraScannerOpen, setIsCameraScannerOpen] = useState<boolean>(false);
    
    // Create a ref to track if component is mounted
    const isMounted = useRef(true);
    
    // Use refs to prevent infinite loops
    const hasSetInitialTransaction = useRef(false);
    const hasCheckedEligibility = useRef(false);
    
    // Form validation functions
    const validateTrackingNumber = (value: string) => {
      if (!value) return 'Tracking number is required';
      return null;
    };
    
    const validateCarrier = (value: string) => {
      if (!value) return 'Carrier is required';
      return null;
    };
    
    const validateTransaction = (value: string) => {
      if (!value) return 'Please select a transaction';
      return null;
    };
    
    // Initialize form with validation
    const {
      values,
      errors,
      touched,
      handleChange,
      handleBlur,
      validateForm,
      setFieldValue
    } = useForm<{
      trackingNumber: string;
      carrier: string;
      transactionId: string;
    }>(
      {
        trackingNumber: '',
        carrier: '',
        transactionId: transactionId || ''
      },
      {
        trackingNumber: validateTrackingNumber,
        carrier: validateCarrier,
        transactionId: validateTransaction
      }
    );
    
    // Filter transactions that don't have tracking, are in escrow, and are not expired
    const eligibleTransactions = transactions.filter(
      (transaction) =>
        !transaction.trackingId &&
        transaction.status === 'in_escrow' &&
        (transaction.isTrackingExpired === undefined ? !isTrackingExpired(transaction) : !transaction.isTrackingExpired)
    );
    
    // Prepare transaction options for select field
    const transactionOptions = eligibleTransactions.map((transaction: Transaction) => ({
      value: transaction.id,
      label: `${transaction.itemDescription} - $${transaction.originalAmount.toFixed(2)}`
    }));
    
    // Prepare carrier options for select field
    const carrierOptions = [
      // { value: 'UPS', label: 'UPS' },
      // { value: 'FedEx', label: 'FedEx' },
      // { value: 'DHL', label: 'DHL' },
      { value: 'australia_post', label: 'Australia Post' },
      // { value: 'startrack', label: 'StarTrack' },
      // { value: 'sendle', label: 'Sendle' },
      { value: 'shippo', label: 'Shippo' },
      //{ value: 'Toll', label: 'Toll' }
    ];
    
    // Cleanup on unmount
    useEffect(() => {
      return () => {
        isMounted.current = false;
      };
    }, []);
  
    // Fetch transactions on component mount
    useEffect(() => {
      console.log('PackageTracking: Initial fetch transactions effect running');
      let isMounted = true;
      const fetchTransactions = async () => {
        try {
          console.log('PackageTracking: Fetching transactions...');
          const result = await getTransactions();
          console.log('PackageTracking: Transactions fetched:', result.length);
          
          // Only update state if component is still mounted
          if (isMounted) {
            showToast('success', 'Transactions loaded successfully');
          }
        } catch (error) {
          if (isMounted) {
            showToast('error', 'Failed to load transactions');
          }
        }
      };
      
      fetchTransactions();
      
      // Cleanup function to prevent state updates after unmount
      return () => {
        isMounted = false;
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array to run only once on mount
    
    // Update selected transaction when transactionId prop changes - only once
    useEffect(() => {
      // Only run this effect if we haven't set the transaction yet or if the transactionId changes
      if (transactionId && (!hasSetInitialTransaction.current || selectedTransaction !== transactionId)) {
        console.log('PackageTracking: Setting selected transaction to:', transactionId);
        setSelectedTransaction(transactionId);
        setFieldValue('transactionId', transactionId);
        hasSetInitialTransaction.current = true;
      }
    }, [transactionId, setFieldValue, selectedTransaction]);
    
    // Check if the selected transaction is in the list of eligible transactions - only once
    useEffect(() => {
      // Only run this effect if we haven't checked eligibility yet and we have transactions and a transactionId
      if (!hasCheckedEligibility.current && transactionId && transactions.length > 0) {
        console.log('PackageTracking: Checking transaction eligibility');
        
        const isEligible = eligibleTransactions.some(t => t.id === transactionId);
        console.log('PackageTracking: Is transaction eligible?', isEligible);
        
        if (!isEligible) {
          console.log('PackageTracking: Selected transaction is not in eligible list, checking all transactions');
          const transaction = transactions.find(t => t.id === transactionId);
          if (transaction) {
            console.log('PackageTracking: Found transaction in all transactions:', transaction);
            // This is just for logging purposes - we don't need to modify the eligibleTransactions
          } else {
            console.log('PackageTracking: Transaction not found in any list');
          }
        }
        
        // Mark that we've checked eligibility
        hasCheckedEligibility.current = true;
      }
    }, [transactionId, eligibleTransactions, transactions]);
    
    // Memoized validation function
    const validateTrackingData = useCallback(async (trackingNumber: string, carrier: string) => {
      if (!trackingNumber || !carrier || !isMounted.current) return;
      
      setIsValidating(true);
      setValidationResult(null);
      
      try {
        const result = await validateTracking(trackingNumber, carrier);
        if (isMounted.current) {
          setValidationResult(result);
        }
      } catch (error) {
        if (isMounted.current) {
          setValidationResult({
            valid: false,
            used: false,
            message: 'Error validating tracking number. Please try again.'
          });
        }
      } finally {
        if (isMounted.current) {
          setIsValidating(false);
        }
      }
    }, [validateTracking]);
    
    // Create a debounced version of the validation function
    const debouncedValidateTrackingData = useCallback(
      debounce((trackingNumber: string, carrier: string) => {
        validateTrackingData(trackingNumber, carrier);
      }, 500),
      [validateTrackingData]
    );
    
    // Generic field blur handler for both input and select elements
    const handleFieldBlur = useCallback((e: React.FocusEvent<HTMLSelectElement>) => {
      // Cast to any to bypass TypeScript's strict type checking
      handleBlur(e as any);
    }, [handleBlur]);
    
    // Handle transaction selection
    const handleTransactionChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
      const transactionId = e.target.value;
      setSelectedTransaction(transactionId);
      setFieldValue('transactionId', transactionId);
    }, [setFieldValue]);
    
    // Custom handlers for select elements
    const handleSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFieldValue(name as keyof typeof values, value);
      
      // If carrier is selected and tracking number is entered, validate the tracking number
      if (name === 'carrier' && value && values.trackingNumber) {
        debouncedValidateTrackingData(values.trackingNumber, value);
      }
    }, [setFieldValue, values, debouncedValidateTrackingData]);
    
    // Custom handleChange function to validate tracking number
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      handleChange(e);
      
      // If tracking number changes and carrier is selected, validate the tracking number
      if (name === 'trackingNumber' && value && values.carrier) {
        debouncedValidateTrackingData(value, values.carrier);
      }
    }, [handleChange, values, debouncedValidateTrackingData]);
    
    // Camera scan handlers
    const handleCameraScanSuccess = useCallback((result: ScanResult) => {
      console.log('Scanned code:', result.text, 'Format:', result.format);
      
      // Set the tracking number
      setFieldValue('trackingNumber', result.text);
      setIsCameraScannerOpen(false);
      
      // If Australia Post tracking was detected and validated, auto-set carrier
      if (result.isValidTracking && result.trackingInfo?.carrier === 'Australia Post') {
        console.log('Valid Australia Post tracking detected:', result.trackingInfo.format);
        setFieldValue('carrier', 'Australia Post');
        
        // Show success message with tracking info
        showToast('success', `${result.trackingInfo.description} detected and validated!`);
        
        // Trigger validation with Australia Post carrier
        debouncedValidateTrackingData(result.text, 'Australia Post');
      } else {
        // Show general success message with format info
        const formatMessage = result.format !== 'UNKNOWN' ? ` (${result.format})` : '';
        showToast('success', `Code scanned successfully${formatMessage}!`);
        
        // Trigger validation if carrier is already selected
        if (values.carrier) {
          debouncedValidateTrackingData(result.text, values.carrier);
        }
      }
    }, [setFieldValue, values.carrier, debouncedValidateTrackingData, showToast]);

    const handleCameraScanError = useCallback((error: string | undefined) => {
      console.error('Camera scan error:', error || 'Unknown error');
      showToast('error', 'Unable to scan code. Please try again or enter manually.');
    }, [showToast]);

    const handleOpenCameraScanner = useCallback(() => {
      setIsCameraScannerOpen(true);
    }, []);

    const handleCloseCameraScanner = useCallback(() => {
      setIsCameraScannerOpen(false);
    }, []);
    
    // Handle form submission
    const handleSubmit = useCallback(async (e: React.FormEvent) => {
      e.preventDefault();
      
      // Validate form
      if (!validateForm()) {
        return;
      }
      
      setIsSubmitting(true);
      
      try {
        // Validate tracking number before saving
        if (!validationResult) {
          // If no validation result exists, validate now
          const result = await validateTracking(values.trackingNumber, values.carrier);
          setValidationResult(result);
          
          // If validation fails, don't proceed
          if (!result.valid) {
            showToast('error', result.message || 'Invalid tracking number. Please check and try again.');
            setIsSubmitting(false);
            return;
          }
        } else if (!validationResult.valid) {
          // If we already have a validation result and it's invalid, don't proceed
          showToast('error', validationResult.message || 'Invalid tracking number. Please check and try again.');
          setIsSubmitting(false);
          return;
        }
        
        // Create tracking data
        const trackingData: Partial<Tracking> = {
          trackingNumber: values.trackingNumber,
          carrier: values.carrier,
          deliveryAddress: {
            recipient: 'John Doe', // Hardcoded for demo
            street: '123 Main St',
            city: 'Melbourne',
            state: 'VIC',
            postalCode: '3000',
            country: 'Australia'
          }
        };
        
        // Add tracking information
        const tracking = await addTrackingInfo(values.transactionId, trackingData);
        
        // Show success message
        showToast('success', 'Tracking information added successfully!');
        
        // Navigate to tracking status
        navigate(`/tracking/${tracking.id}`);
      } catch (error) {
        showToast('error', 'Failed to add tracking information. Please try again.');
        setIsSubmitting(false);
      }
    }, [validateForm, values, addTrackingInfo, showToast, navigate, validationResult, validateTracking]);
    
    // Render the form or empty state
    return (
      <div className="package-tracking-container">
        {eligibleTransactions.length > 0 ? (
          <div className="package-tracking-form">
            {/* Add the time remaining indicator */}
            {selectedTransaction && (
              <TimeRemainingIndicator
                transaction={transactions.find(t => t.id === selectedTransaction)!}
              />
            )}
            <form onSubmit={handleSubmit}>
              <FormField
                label="Select Transaction"
                error={errors.transactionId}
                touched={touched.transactionId}
              >
                <SelectField
                  name="transactionId"
                  value={selectedTransaction}
                  onChange={handleTransactionChange}
                  onBlur={handleFieldBlur}
                  error={errors.transactionId}
                  touched={touched.transactionId}
                  options={transactionOptions}
                  placeholder="Select a transaction"
                  disabled={!!transactionId}
                />
              </FormField>

              <FormField
                label="Carrier"
                error={errors.carrier}
                touched={touched.carrier}
              >
                <SelectField
                  name="carrier"
                  value={values.carrier}
                  onChange={handleSelectChange}
                  onBlur={handleFieldBlur}
                  error={errors.carrier}
                  touched={touched.carrier}
                  options={carrierOptions}
                  placeholder="Select a carrier"                  
                />
              </FormField>
              
              <FormField
                label="Tracking Number"
                error={errors.trackingNumber}
                touched={touched.trackingNumber}
              >
                <div className="input-with-validation">
                  <div className="input-wrapper">
                    <input
                      type="text"
                      className={`input-field ${errors.trackingNumber && touched.trackingNumber ? 'input-error' : ''}`}
                      placeholder="Enter tracking number"
                      name="trackingNumber"
                      value={values.trackingNumber}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      aria-invalid={errors.trackingNumber && touched.trackingNumber ? 'true' : 'false'}
                      aria-describedby={validationResult ? 'tracking-validation-message' : undefined}
                    />
                    
                    {/* Camera Scan Button */}
                    <CameraScanButton
                      onClick={handleOpenCameraScanner}
                      disabled={isValidating}
                      className="tracking-camera-btn"
                    />
                    
                    {isValidating && (
                      <div className="validation-indicator spinner" aria-label="Validating tracking number">
                        <div className="spinner-icon"></div>
                      </div>
                    )}
                    {!isValidating && validationResult && validationResult.valid && (
                      <div className="validation-indicator valid" aria-label="Tracking number is valid">
                        <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" fill="none">
                          <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  {validationResult && !validationResult.valid && (
                    <div
                      id="tracking-validation-message"
                      className="validation-message error"
                      aria-live="polite"
                    >
                      {validationResult.message}
                    </div>
                  )}
                  {validationResult && validationResult.valid && validationResult.used && (
                    <div
                      id="tracking-validation-message"
                      className="validation-message warning"
                      aria-live="polite"
                    >
                      {validationResult.message}
                    </div>
                  )}
                </div>
              </FormField>
              
              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting || (validationResult !== null && !validationResult.valid)}
              >
                <Text className="text-body text-white">
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Text>
              </button>
            </form>
          </div>
        ) : (
          <EmptyState
            message={
              transactions.some(t => !t.trackingId && t.status === 'in_escrow' &&
                (t.isTrackingExpired === undefined ? isTrackingExpired(t) : t.isTrackingExpired))
                ? "Time window expired"
                : "No eligible transactions"
            }
            subMessage={
              transactions.some(t => !t.trackingId && t.status === 'in_escrow' &&
                (t.isTrackingExpired === undefined ? isTrackingExpired(t) : t.isTrackingExpired))
                ? "The time window to provide tracking information has expired. The transaction will be marked as cancelled."
                : "You don't have any transactions that need tracking information"
            }
          />
        )}
        
        {/* Camera Scanner Modal */}
        <CameraScanner
          isOpen={isCameraScannerOpen}
          onClose={handleCloseCameraScanner}
          onScanSuccess={handleCameraScanSuccess}
          onScanError={handleCameraScanError}
          timeoutMs={30000}
          enableBarcodeScanning={true}
          validateAustraliaPost={true}
        />
      </div>
    );
};